import { useEffect, useState } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { resolveStorageUrl } from "@/utils/storageUrl";

export interface Festival {
  id: string
  name: string
  name_en?: string | null
  description: string | null
  description_en?: string | null
  location: string | null
  location_en?: string | null
  start_date: string | null
  end_date: string | null
  image_url: string | null
  current_participants: number
  capacity: number | null
  ticket_url: string | null
  website_url: string | null
  status: string
  is_featured: boolean
  theme: string | null
  is_external: boolean | null
  external_link: string | null
  price_display: string | null
  hero_stats?: Array<{ value: string; label: string }> | null
  slug?: string | null
  organized_by_us?: boolean | null
}

export const useFestivals = () => {
  const [festivals, setFestivals] = useState<Festival[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchFestivals = async () => {
      try {
        // Fetch festivals
        const { data: festivalsData, error: festivalsError } = await supabase
          .from('festivals')
          .select('*')
          .in('status', ['upcoming', 'ongoing', 'past', 'coming_soon'])
          .neq('theme', 'family-hostel')
          .neq('theme', 'daycamp')
          .or('is_published.is.null,is_published.eq.true')
          .order('start_date', { ascending: true })
          .limit(100)

        if (festivalsError) throw festivalsError
        
        // Fetch thumbnail and hero images for these festivals
        const festivalIds = (festivalsData || []).map(f => f.id)
        const { data: eventImages } = await supabase
          .from('event_images')
          .select('event_id, file_path, is_primary, category, display_order, created_at')
          .in('event_id', festivalIds)
          .in('category', ['thumbnail', 'hero', 'general'])
          .order('display_order', { ascending: true })
        
        // Process data with improved thumbnail selection logic
        const processedData = (festivalsData || []).map(festival => {
          const festivalImages = eventImages?.filter(img => img.event_id === festival.id) || []
          
          // Priority order: thumbnail+primary > first thumbnail > hero+primary > first hero > first general
          const primaryThumbnail = festivalImages.find(img => img.category === 'thumbnail' && img.is_primary)
          const firstThumbnail = festivalImages.find(img => img.category === 'thumbnail')
          const primaryHero = festivalImages.find(img => img.category === 'hero' && img.is_primary)
          const firstHero = festivalImages.find(img => img.category === 'hero')
          const firstGeneral = festivalImages.find(img => img.category === 'general')
          
          const selectedImage = primaryThumbnail || firstThumbnail || primaryHero || firstHero || firstGeneral
          
          return {
            ...festival,
            image_url: selectedImage ? 
              resolveStorageUrl(selectedImage.file_path) :
              festival.image_url
          }
        })
        
        setFestivals(processedData)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred')
      } finally {
        setLoading(false)
      }
    }

    fetchFestivals()
  }, [])

  return { festivals, loading, error }
}

export const useFeaturedFestivals = () => {
  const [festivals, setFestivals] = useState<Festival[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchFestivals = async () => {
      try {
        // Fetch festivals
        const { data: festivalsData, error: festivalsError } = await supabase
          .from('festivals')
          .select('*')
          .eq('is_featured', true)
          .eq('status', 'upcoming')
          .neq('theme', 'family-hostel')
          .neq('theme', 'daycamp')
          .or('is_published.is.null,is_published.eq.true')
          .order('start_date', { ascending: true })
          .limit(3)

        if (festivalsError) throw festivalsError
        
        // Fetch thumbnail and hero images for these festivals  
        const festivalIds = (festivalsData || []).map(f => f.id)
        const { data: eventImages } = await supabase
          .from('event_images')
          .select('event_id, file_path, is_primary, category, display_order, created_at')
          .in('event_id', festivalIds)
          .in('category', ['thumbnail', 'hero', 'general'])
          .order('display_order', { ascending: true })
        
        // Process data with improved thumbnail selection logic
        const processedData = (festivalsData || []).map(festival => {
          const festivalImages = eventImages?.filter(img => img.event_id === festival.id) || []
          
          // Priority order: thumbnail+primary > first thumbnail > hero+primary > first hero > first general
          const primaryThumbnail = festivalImages.find(img => img.category === 'thumbnail' && img.is_primary)
          const firstThumbnail = festivalImages.find(img => img.category === 'thumbnail')
          const primaryHero = festivalImages.find(img => img.category === 'hero' && img.is_primary)
          const firstHero = festivalImages.find(img => img.category === 'hero')
          const firstGeneral = festivalImages.find(img => img.category === 'general')
          
          const selectedImage = primaryThumbnail || firstThumbnail || primaryHero || firstHero || firstGeneral
          
          return {
            ...festival,
            image_url: selectedImage ? 
              resolveStorageUrl(selectedImage.file_path) :
              festival.image_url
          }
        })
        
        setFestivals(processedData)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred')
      } finally {
        setLoading(false)
      }
    }

    fetchFestivals()
  }, [])

  return { festivals, loading, error }
}